import { create } from 'zustand';
import { supabase, Institution, Simulation, Module } from './supabase';

interface AdminState {
    institutions: Institution[];
    simulations: Simulation[];
    modules: Module[];
    isLoading: boolean;
    error: string | null;

    // Actions
    fetchInstitutions: () => Promise<void>;
    fetchSimulations: (institutionId?: string) => Promise<void>;

    addInstitution: (name: string, logoFile?: File) => Promise<{ success: boolean; error?: string }>;
    updateInstitution: (id: string, updates: Partial<Institution>, logoFile?: File) => Promise<{ success: boolean; error?: string }>;

    addSimulation: (institutionId: string, title: string, config: any) => Promise<{ success: boolean; error?: string }>;
    updateSimulation: (id: string, updates: Partial<Simulation>) => Promise<{ success: boolean; error?: string }>;
    deleteSimulation: (id: string) => Promise<{ success: boolean; error?: string }>;

    // Module Actions
    fetchModules: (institutionId: string) => Promise<void>;
    addModule: (institutionId: string, title: string, description: string, items: any[]) => Promise<{ success: boolean; error?: string }>;
    updateModule: (id: string, updates: Partial<Module>) => Promise<{ success: boolean; error?: string }>;
    deleteModule: (id: string) => Promise<{ success: boolean; error?: string }>;

    uploadLogo: (file: File) => Promise<{ url: string | null; error: string | null }>;
    createUser: (userData: any) => Promise<{ success: boolean; error?: string }>;
    deleteUser: (userId: string) => Promise<{ success: boolean; error?: string }>;

    // UI Handling
    selectedInstitution: Institution | null;
    setSelectedInstitution: (inst: Institution | null) => void;
}

export const useAdminStore = create<AdminState>((set, get) => ({
    institutions: [],
    simulations: [],
    modules: [],
    isLoading: false,
    error: null,
    selectedInstitution: null,

    setSelectedInstitution: (inst) => set({ selectedInstitution: inst }),

    fetchInstitutions: async () => {
        set({ isLoading: true, error: null });
        try {
            if (!supabase) throw new Error('Supabase client not initialized');
            const { data, error } = await supabase
                .from('institutions')
                .select('*')
                .order('name');

            if (error) throw error;
            set({ institutions: data || [] });
        } catch (error: any) {
            set({ error: error.message });
        } finally {
            set({ isLoading: false });
        }
    },

    fetchSimulations: async (institutionId) => {
        set({ isLoading: true, error: null });
        try {
            if (!supabase) throw new Error('Supabase client not initialized');

            let query = supabase.from('simulations').select('*').order('created_at', { ascending: false });

            if (institutionId) {
                query = query.eq('institution_id', institutionId);
            }

            const { data, error } = await query;

            if (error) throw error;
            set({ simulations: data || [] });
        } catch (error: any) {
            set({ error: error.message });
        } finally {
            set({ isLoading: false });
        }
    },

    uploadLogo: async (file: File) => {
        try {
            if (!supabase) throw new Error('Supabase client not initialized');

            const fileExt = file.name.split('.').pop();
            const fileName = `${Date.now()}.${fileExt}`;
            const filePath = `${fileName}`;

            const { error: uploadError } = await supabase.storage
                .from('institution-logos')
                .upload(filePath, file);

            if (uploadError) throw uploadError;

            const { data } = supabase.storage
                .from('institution-logos')
                .getPublicUrl(filePath);

            return { url: data.publicUrl, error: null };
        } catch (error: any) {
            return { url: null, error: error.message };
        }
    },

    addInstitution: async (name, logoFile) => {
        set({ isLoading: true, error: null });
        try {
            if (!supabase) throw new Error('Supabase client not initialized');

            let logoUrl = null;
            if (logoFile) {
                try {
                    const { url, error: uploadError } = await get().uploadLogo(logoFile);
                    if (uploadError) {
                        console.error('Logo upload failed:', uploadError);
                        // Don't throw, just define logoUrl as null or continue
                    } else {
                        logoUrl = url;
                    }
                } catch (uploadErr) {
                    console.error('Logo upload exception:', uploadErr);
                }
            }

            const { error } = await supabase
                .from('institutions')
                .insert([{ name, logo_url: logoUrl, is_active: true }]);

            if (error) throw error;

            await get().fetchInstitutions(); // Refresh list
            return { success: true };
        } catch (error: any) {
            set({ error: error.message });
            return { success: false, error: error.message };
        } finally {
            set({ isLoading: false });
        }
    },

    updateInstitution: async (id, updates, logoFile) => {
        set({ isLoading: true, error: null });
        try {
            if (!supabase) throw new Error('Supabase client not initialized');

            let logoUrl = updates.logo_url;
            console.log('Initial logoUrl:', logoUrl);
            console.log('logoFile:', logoFile);

            if (logoFile) {
                const { url, error: uploadError } = await get().uploadLogo(logoFile);
                console.log('Upload result - url:', url, 'error:', uploadError);
                if (uploadError) throw new Error(uploadError);
                if (url) logoUrl = url;
            }

            console.log('Final logoUrl to save:', logoUrl);

            const updateData = { ...updates, logo_url: logoUrl };
            console.log('Update data:', updateData);

            const { error } = await supabase
                .from('institutions')
                .update(updateData)
                .eq('id', id);

            if (error) throw error;

            await get().fetchInstitutions();
            return { success: true };
        } catch (error: any) {
            set({ error: error.message });
            return { success: false, error: error.message };
        } finally {
            set({ isLoading: false });
        }
    },

    addSimulation: async (institutionId, title, config) => {
        set({ isLoading: true, error: null });
        try {
            if (!supabase) throw new Error('Supabase client not initialized');

            const { error } = await supabase
                .from('simulations')
                .insert([{ institution_id: institutionId, title, config, is_active: true }]);

            if (error) throw error;

            await get().fetchSimulations(institutionId);
            return { success: true };
        } catch (error: any) {
            set({ error: error.message });
            return { success: false, error: error.message };
        } finally {
            set({ isLoading: false });
        }
    },

    updateSimulation: async (id, updates) => {
        set({ isLoading: true, error: null });
        try {
            if (!supabase) throw new Error('Supabase client not initialized');

            const { error } = await supabase
                .from('simulations')
                .update(updates)
                .eq('id', id);

            if (error) throw error;

            return { success: true };
        } catch (error: any) {
            set({ error: error.message });
            return { success: false, error: error.message };
        } finally {
            set({ isLoading: false });
        }
    },

    deleteSimulation: async (id) => {
        // Implement delete logic
        set({ isLoading: true, error: null });
        try {
            if (!supabase) throw new Error('Supabase client not initialized');
            const { error } = await supabase.from('simulations').delete().eq('id', id);
            if (error) throw error;
            return { success: true };
        } catch (error: any) {
            set({ error: error.message });
            return { success: false, error: error.message };
        } finally {
            set({ isLoading: false });
        }
    },

    createUser: async (userData) => {
        set({ isLoading: true, error: null });
        try {
            if (!supabase) throw new Error('Supabase client not initialized');

            // 1. Supabase Auth ile kullanıcı oluştur (şifre doğru hash'lenir)
            const { data: authData, error: authError } = await supabase.auth.signUp({
                email: userData.email,
                password: userData.password,
                options: {
                    data: {
                        full_name: userData.full_name
                    }
                }
            });

            if (authError) throw authError;
            if (!authData.user) throw new Error('Kullanıcı oluşturulamadı');

            // 2. Profil bilgilerini ayarla (rol ve kurum)
            const { error: profileError } = await supabase.rpc('setup_user_profile', {
                user_id: authData.user.id,
                user_email: userData.email,
                user_full_name: userData.full_name,
                user_role: userData.role,
                user_institution_id: userData.institution_id
            });

            if (profileError) {
                console.error('Profile setup error:', profileError);
                // Kullanıcı oluşturuldu ama profil ayarlanamadı - yine de başarılı say
            }

            return { success: true };
        } catch (error: any) {
            const errorMessage = error.message || 'Kullanıcı oluşturulamadı';
            set({ error: errorMessage });
            return { success: false, error: errorMessage };
        } finally {
            set({ isLoading: false });
        }
    },

    deleteUser: async (userId) => {
        set({ isLoading: true, error: null });
        try {
            if (!supabase) throw new Error('Supabase client not initialized');

            const { error } = await supabase.rpc('admin_delete_user', {
                user_id: userId
            });

            if (error) throw error;

            return { success: true };
        } catch (error: any) {
            const errorMessage = error.message || 'Kullanıcı silinemedi';
            set({ error: errorMessage });
            return { success: false, error: errorMessage };
        } finally {
            set({ isLoading: false });
        }
    },

    // ========== MODULE CRUD ==========
    fetchModules: async (institutionId) => {
        set({ isLoading: true, error: null });
        try {
            if (!supabase) throw new Error('Supabase client not initialized');
            const { data, error } = await supabase
                .from('modules')
                .select('*')
                .eq('institution_id', institutionId)
                .order('created_at', { ascending: false });

            if (error) throw error;
            set({ modules: (data || []).map((m: any) => ({ ...m, items: m.items || [] })) });
        } catch (error: any) {
            set({ error: error.message });
        } finally {
            set({ isLoading: false });
        }
    },

    addModule: async (institutionId, title, description, items) => {
        set({ isLoading: true, error: null });
        try {
            if (!supabase) throw new Error('Supabase client not initialized');
            const { error } = await supabase
                .from('modules')
                .insert([{ institution_id: institutionId, title, description, items, is_active: true }]);

            if (error) throw error;
            await get().fetchModules(institutionId);
            return { success: true };
        } catch (error: any) {
            set({ error: error.message });
            return { success: false, error: error.message };
        } finally {
            set({ isLoading: false });
        }
    },

    updateModule: async (id, updates) => {
        set({ isLoading: true, error: null });
        try {
            if (!supabase) throw new Error('Supabase client not initialized');
            const { error } = await supabase
                .from('modules')
                .update(updates)
                .eq('id', id);

            if (error) throw error;
            return { success: true };
        } catch (error: any) {
            set({ error: error.message });
            return { success: false, error: error.message };
        } finally {
            set({ isLoading: false });
        }
    },

    deleteModule: async (id) => {
        set({ isLoading: true, error: null });
        try {
            if (!supabase) throw new Error('Supabase client not initialized');
            const { error } = await supabase.from('modules').delete().eq('id', id);
            if (error) throw error;
            return { success: true };
        } catch (error: any) {
            set({ error: error.message });
            return { success: false, error: error.message };
        } finally {
            set({ isLoading: false });
        }
    }
}));

